import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { USAVenezuelaMap } from '../graphics/USAVenezuelaMap';
import { GridPattern } from '../graphics/GridPattern';
import { Button } from '../ui/Button';
import { ShieldCheckIcon, LanguagesIcon, FileCheck2Icon, FactoryIcon, ArrowRightIcon } from 'lucide-react';
import { useLang } from '../../lib/i18n/LanguageContext';
import { dictionary } from '../../lib/i18n/dictionary';
const badgeIcons = [ShieldCheckIcon, LanguagesIcon, FileCheck2Icon, FactoryIcon];
export function Hero() {
  const {
    lang
  } = useLang();
  const t = dictionary.hero;
  const nav = dictionary.nav;
  const badges = t.badges[lang];
  return <section id="top" className="relative min-h-[100svh] bg-navy-deep overflow-hidden pt-24 pb-16" data-id="element-376">
      <div className="absolute inset-0 tech-grid opacity-60" aria-hidden="true" data-id="element-377" />
      <GridPattern className="absolute inset-0 text-gold/40 opacity-20" data-id="element-378" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(27,58,107,0.4),_transparent_60%)]" aria-hidden="true" data-id="element-379" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(201,169,97,0.08),_transparent_50%)]" aria-hidden="true" data-id="element-380" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center" data-id="element-381">
        <div className="lg:col-span-7" data-id="element-382">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex items-center gap-2.5 mb-6" data-id="element-383">
            <span className="h-px w-10 bg-gold" data-id="element-384" />
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-gold" data-id="element-385">
              {nav.privateTradeDesk[lang]}
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.05
        }} className="font-serif text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight" data-id="element-386">
            {t.headlineStart[lang]}{' '}
            <span className="text-gold" data-id="element-387">{t.headlineEnd[lang]}</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="mt-6 text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed" data-id="element-388">
            {t.subheadline[lang]}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.25
        }} className="mt-9 flex flex-wrap gap-3" data-id="element-389">
            <a href="#supplier-form" data-id="element-390">
              <Button variant="gold" size="lg" data-id="element-391">
                {t.ctaSupplier[lang]}
                <ArrowRightIcon className="w-4 h-4" data-id="element-392" />
              </Button>
            </a>
            <a href="#buyer-form" data-id="element-393">
              <Button variant="primary" size="lg" data-id="element-394">
                {t.ctaBuyer[lang]}
              </Button>
            </a>
            <Link to="/contact" data-id="element-395">
              <Button variant="secondary" size="lg" data-id="element-396">
                {t.ctaAccess[lang]}
              </Button>
            </Link>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="mt-6 text-xs md:text-sm text-slate-400 max-w-xl" data-id="element-397">
            {t.trustLine[lang]}
          </motion.p>

          <motion.ul initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.5
        }} className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl" data-id="element-398">
            {badges.map((label, i) => {
            const Icon = badgeIcons[i];
            return <li key={label} data-id="element-399">
                  <a href="#compliance" className="flex items-start gap-2.5 px-3.5 py-3 border border-white/10 bg-white/[0.02] hover:border-gold/40 hover:bg-white/[0.04] rounded-md transition-colors" data-id="element-400">
                    <Icon className="w-4 h-4 text-gold shrink-0 mt-0.5" data-id="element-401" />
                    <span className="text-[12.5px] text-slate-200 leading-snug" data-id="element-402">
                      {label}
                    </span>
                  </a>
                </li>;
          })}
          </motion.ul>
        </div>

        <div className="lg:col-span-5 relative" data-id="element-403">
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.9,
          delay: 0.2
        }} className="relative aspect-[4/3] border border-white/10 rounded-md overflow-hidden bg-gradient-to-br from-navy-800/50 to-navy-deep" data-id="element-404">
            <USAVenezuelaMap className="absolute inset-0 w-full h-full" data-id="element-405" />
            <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 bg-black/40 border border-white/10 rounded-sm" data-id="element-406">
              <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" data-id="element-407" />
              <span className="font-mono text-[10px] text-slate-300 tracking-wider" data-id="element-408">
                {t.liveCorridor[lang]}
              </span>
            </div>
            <div className="absolute bottom-3 right-3 font-mono text-[10px] text-slate-400 tracking-wider" data-id="element-409">
              N 29.7° / N 10.5°
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" data-id="element-410" />
    </section>;
}